import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import {
  Close,
  Send,
  PlayArrow,
  Pause,
  InsertDriveFile
} from '@mui/icons-material';

// Utility function to determine file type
const getMediaType = (file) => {
  if (file?.type.startsWith('image/')) return 'image';
  if (file?.type.startsWith('video/')) return 'video';
  if (file?.type.startsWith('audio/')) return 'audio';
  return 'document';
};

const MediaPreview = ({ file, caption, onSend, onClose, onCaptionChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const mediaType = getMediaType(file);

  const fileUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    if (!fileUrl) return;
    return () => {
      URL.revokeObjectURL(fileUrl);
    };
  }, [fileUrl]);

  const handlePlayPause = () => {
    const mediaElement = videoRef.current || audioRef.current;
    if (mediaElement) {
      if (isPlaying) {
        mediaElement.pause();
      } else {
        mediaElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSend = () => {
    if (file) onSend(file, caption || '');
  };

  if (!file || !fileUrl) return null;

  const renderMediaContent = () => {
    switch (mediaType) {
      case 'image':
        return (
          <img
            src={fileUrl}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        );

      case 'video':
        return (
          <video
            ref={videoRef}
            src={fileUrl}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '8px'
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            controls
          />
        );

      case 'audio':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 2,
              width: '100%'
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: '#00BFA5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={handlePlayPause}
            >
              {isPlaying ? (
                <Pause sx={{ fontSize: 40, color: 'white' }} />
              ) : (
                <PlayArrow sx={{ fontSize: 40, color: 'white' }} />
              )}
            </Box>
            <Typography variant="body1" color="textSecondary">
              Audio File ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
            <audio
              ref={audioRef}
              src={fileUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              style={{ display: 'none' }}
            />
          </Box>
        );

      case 'document':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              width: '100%'
            }}
          >
            <Paper
              sx={{
                width: 100,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e3f2fd'
              }}
            >
              <InsertDriveFile sx={{ fontSize: 40, color: '#1976d2' }} />
            </Paper>
            <Typography variant="body1" fontWeight="bold">
              {file.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Send {mediaType}
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Media Preview */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          maxHeight: '400px',
          p: 2,
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        {renderMediaContent()}
      </Box>

      {/* Caption Input */}
      {mediaType !== 'audio' && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            flexShrink: 0,
            minHeight: '100px'
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={3}
            maxRows={5}
            placeholder="Add a caption..."
            value={caption || ''}
            onChange={(e) => onCaptionChange(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: '#1976d2' },
                '&.Mui-focused fieldset': { borderColor: '#1976d2' }
              }
            }}
          />
        </Box>
      )}

      {/* Action Buttons */}
      <DialogActions sx={{ p: 2, pt: 0, flexShrink: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: '20px', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={<Send />}
          sx={{ borderRadius: '20px', textTransform: 'none' }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaPreview;